import { ResourceCommentViewModel, ResourceLikesViewModel, VideoLibraryView } from '@models/api';
import { data, LoaderFunctionArgs } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import { isNullOrEmpty, mixpanelService } from '@utilities';
import VideoItemDesktop from './desktop/video-item';
import VideoItemMobile from './mobile/video-item';
import { generateSHA256Hex } from '@server';
import { HeaderProps } from '@models/ui';
import { NotFound404 } from '@components';
import { useEffect } from 'react';

export default function Index() {
  const loaderData = useLoaderData<typeof loader>();

  // const [isShareOpen, setIsShareOpen] = useState<boolean>(false);
  // const navigate = useNavigate();

  const headerProps: HeaderProps = {
    isLoggedIn: loaderData.isAuthenticated,
    position: 'top_nav_homepage',
    customerName: loaderData.user?.name ?? '',
    customerImageUrl: loaderData.user?.profilePic ?? '',
    showManageEvaluations: true,
    isSubscribed: loaderData.user?.isSubscribed ?? false,
  };

  useEffect(() => {
    const video = loaderData.selectedVideo;
    if (!video) return;
    let userStatus = 'visitor';
    if (loaderData.isAuthenticated) userStatus = 'registered';
    if (loaderData.user?.isSubscribed ?? false) userStatus = 'subscriber';
    mixpanelService.track('video_open_click', {
      video_name: video.header,
      tag_name: !isNullOrEmpty(video.resultCategory) ? video.resultCategory : video.tags[0],
      user_status: userStatus,
    });
  }, []);

  // If we have an error response, render the 404 component
  if (loaderData.notFound) {
    return <NotFound404 />;
  }

  /* const handleShareClick = () => {
      const pageLink = window.location.href;
      navigator.clipboard.writeText(pageLink); // Copy the link to clipboard
      setIsPopupLinkVisible(true); // Show the popup
      setIsShareOpen(!isShareOpen);
      setTimeout(() => setIsPopupLinkVisible(false), 3000); // Hide after 3 seconds
    };
   */

  return !loaderData.isMobile ? (
    <VideoItemDesktop
      selectedVideo={loaderData.selectedVideo}
      suggestedVideos={loaderData.suggestedVideos}
      headerProps={headerProps}
      isMobile={loaderData.isMobile}
      likes={loaderData.likes}
      isLiked={loaderData.isLiked}
      comments={loaderData.comments}            
    />
  ) : (
    <VideoItemMobile
      selectedVideo={loaderData.selectedVideo}
      suggestedVideos={loaderData.suggestedVideos}
      headerProps={headerProps}
      isMobile={loaderData.isMobile}
      likes={loaderData.likes}
      isLiked={loaderData.isLiked}
      comments={loaderData.comments}      
    />
  );
}

export async function loader({ context, params }: LoaderFunctionArgs) {
  let selectedVideo: VideoLibraryView | null = null;
  let suggestedVideos: VideoLibraryView[] = [];

  let likes: ResourceLikesViewModel = {
    noOfLikes: 0,
  };
  let comments: ResourceCommentViewModel[] = [];
  let isLiked: boolean = false;

  const slug = params.slug;

  if (isNullOrEmpty(slug)) {
    return { selectedVideo: null, suggestedVideos: [], notFound: true, ...context.clientContext, likes, isLiked, comments };
  }

  try {
    const service = context.serverContext.apiClient.customerService;
    const resp = await service.GetVideoLibraryList();
    const videoList = resp.data;
    const selectedVideoIndex = videoList.findIndex(x => x.slug === slug);
    // If video not found, return not found response
    if (selectedVideoIndex === -1) {
      return { selectedVideo: null, suggestedVideos: [], notFound: true, ...context.clientContext, likes, isLiked, comments };
    }

    selectedVideo = videoList[selectedVideoIndex];

    const currentTime = Math.floor(Date.now() / 1000);
    const expirationTime = currentTime + 60 * 60; // 60 minutes * 60 seconds
    const expiration = expirationTime.toString();
    const libraryId = context.serverContext.appData.secrets.VideoStream.LibraryId || '';
    const bunnyTokenKey: string = context.serverContext.appData.secrets.VideoStream.TokenAuthKey || '';
    const videoId =
      selectedVideo.knowledgeHubVideoType == 'free' || (context.clientContext?.isAuthenticated && context.clientContext.user?.isSubscribed)
        ? selectedVideo.videoId
        : selectedVideo.trailerVideoId;
    if (!videoId) {
      return {
        selectedVideo: null,
        suggestedVideos: [],
        notFound: true,
        ...context.clientContext,
        likes,
        isLiked,
        comments,
      };
    }
    const hashValue = generateSHA256Hex(bunnyTokenKey, videoId, expiration);

    selectedVideo.hashValue = hashValue;
    selectedVideo.expiry = expiration;
    selectedVideo.libraryId = libraryId;
    selectedVideo.videoId = videoId; // Reset selectedVideo.videoId for security. If it's trailer, client does not need to know main video id. Also Video IFrame can use same `videoId` property irrespective of main or trailer.

    // Get suggested videos (next 7 or wrap around)
    const numVideos = videoList.length;
    const maxSuggestions = 7;

    if (numVideos <= 1) {
      // No suggestions if only one video exists
      suggestedVideos = [];
    } else if (selectedVideoIndex + maxSuggestions < numVideos) {
      // Simple case: take next 7 videos
      suggestedVideos = videoList.slice(selectedVideoIndex + 1, selectedVideoIndex + 1 + maxSuggestions);
    } else {
      // Need to wrap around
      // First take remaining videos after the selected one
      suggestedVideos = videoList.slice(selectedVideoIndex + 1);

      // If we still need more videos to reach 7, take from the beginning
      // But skip the selected video
      const remaining = maxSuggestions - suggestedVideos.length;
      if (remaining > 0) {
        const videosFromBeginning = videoList.slice(0, selectedVideoIndex);
        suggestedVideos = suggestedVideos.concat(videosFromBeginning.slice(0, remaining));
      }
    }

    const likesResp = service.GetResourceLikes(selectedVideo.id);
    const commentsResp = service.GetResourceComments(selectedVideo.id);
    const responses = await Promise.all([likesResp, commentsResp]);

    if (context.clientContext.isAuthenticated) {
      const isLikedResp = await service.IsLiked(selectedVideo.id);
      isLiked = isLikedResp.data;
    }

    likes = responses[0].data;

    comments = responses[1].data;

    if (!(context.clientContext?.isAuthenticated && context.clientContext.user?.isSubscribed)) {
      for (const video of suggestedVideos) {
        if (video.knowledgeHubVideoType == 'free') continue;
        video.videoId = video.trailerVideoId ?? '';
      }
    }
  } catch (e) {
    console.log(e);
    return {
      selectedVideo: null,
      suggestedVideos: [],
      notFound: true,
      ...context.clientContext,
      likes,
      isLiked,
      comments,
    };
  }

  const headers = new Headers({
    //  "Set-Cookie": await destroySession(session),
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
    'Access-Control-Allow-Headers': 'Content-Type, Authorization, X-Requested-With',
    'Access-Control-Allow-Credentials': 'true',
    'Access-Control-Max-Age': '86400',
    Vary: 'Origin',
    'Access-Control-Expose-Headers': 'Content-Range, Accept-Ranges',
  });
  return data({ selectedVideo, suggestedVideos, notFound: false, ...context.clientContext, likes, isLiked, comments }, { headers });
}
